import { makeAutoObservable, runInAction } from 'mobx';
import agent from '../services/api/agent';
import { ICourseExportPackages, INewPageModel, TCourseViewModel } from '../services/models/CourseViewModels';
import { ICopyPageObject } from '../services/models/OwnerViewModel';
import { ICourseProperties, TPropertyValueView } from '../services/models/PropertyViewModel';
import SnackbarUtils from '../components/snackbarUtils/SnackbarUtilsConfigurator';
import { store } from './store';
import { IPageProperties } from '../services/models/PageViewModel';

export enum eExportType {
  scorm12 = '1.2',
  html = 'html',
}
export default class CourseStore {
  courseViewModel: TCourseViewModel | undefined = undefined;
  selectedCourseId = 0;
  courseProperties: ICourseProperties | undefined = undefined;
  pageProperties: IPageProperties | undefined = undefined;
  coursePageTempTitle = '';
  pagePropertiesPageID = 0;
  newPageCoursePageID = '0';
  loading = false;
  loadingInitial = true;
  loadingExtra = false;
  uploading = false;

  constructor() {
    makeAutoObservable(this);
  }

  loadCourseViewModel = async (courseId: number) => {
    return await agent.Course.getPages(courseId)
      .then((courseViewModel) => {
        try {
          runInAction(async () => {
            this.courseViewModel = courseViewModel;
            await this.getCourseProperties(courseViewModel.courseID);
          });
          this.setLoadingInitial(false);
          return this.courseViewModel;
        } catch (error) {
          console.log(error);
          this.courseViewModel = undefined;
          return undefined;
        }
      })
      .catch(() => {
        this.courseViewModel = undefined;
        return undefined;
      });
  };

  clearCourseViewModel = () => {
    this.resetNewPageCoursePageID();
    this.courseViewModel = undefined;
    this.pageProperties = undefined;
  };

  getCourseProperties = async (courseId: number) => {
    if (this.courseProperties?.courseID === courseId) return;
    const newCourseProperties = await agent.Course.getProperties(courseId);
    try {
      runInAction(() => {
        this.courseProperties = newCourseProperties;
        this.selectedCourseId = courseId;
      });
    } catch (error) {
      console.log(error);
    }
    return newCourseProperties;
  };

  updateCourseProperties = async (courseId: number) => {
    const newCourseProperties = await agent.Course.getProperties(courseId);
    try {
      runInAction(() => {
        this.courseProperties = newCourseProperties;
      });
    } catch (error) {
      console.log(error);
    }
  };

  uploadCoverPhoto = async (propertyValueView: TPropertyValueView, file: Blob) => {
    await agent.Property.uploadPhoto(propertyValueView.propertyValueID, 'Cover Photo', file);
    if (this.selectedCourseId != 0) {
      await this.updateCourseProperties(this.selectedCourseId);
      if (store.ownerStore.courseList === undefined) return;
      for (let i = 0; i < store.ownerStore.courseList.length; i++) {
        if (store.ownerStore.courseList[i].id == this.selectedCourseId && this.courseProperties) {
          store.ownerStore.courseList[i].course.staticInfoGroup.items.avatar.value = this.courseProperties.settings.items.avatar.option.value; //Updates avatar in courselist on ownerstore
        }
      }
    }
  };

  changeCoverPhoto = async (propertyValueView: TPropertyValueView, value: string) => {
    await store.propertyStore.setOption(propertyValueView, value).then((res) => {
      propertyValueView.option.value = res.value;
    });
    if (this.selectedCourseId != 0) {
      await this.updateCourseProperties(this.selectedCourseId);
      if (store.ownerStore.courseList === undefined) return;
      for (let i = 0; i < store.ownerStore.courseList.length; i++) {
        if (store.ownerStore.courseList[i].id == this.selectedCourseId && this.courseProperties) {
          store.ownerStore.courseList[i].course.staticInfoGroup.items.avatar.value = this.courseProperties.settings.items.avatar.option.value; //Updates avatar in courselist on ownerstore
        }
      }
    }
  };

  movePage = async (coursePageID: string, parentCoursePageID: string, index?: number) => {
    if (!this.courseViewModel) return false;

    try {
      await agent.Course.movePage(this.courseViewModel.courseID, coursePageID, parentCoursePageID, index !== undefined ? index + 1 : 1);
      return true;
    } catch (error) {
      console.error('error moving page:', error);
      return false;
    }
  };

  newPage = async (index: number, parentCoursePageID?: number) => {
    if (this.courseViewModel) {
      const newPageRequest: INewPageModel = { courseID: this.courseViewModel.courseID, parentCoursePageID: parentCoursePageID, index: index, title: 'New Page', templateNo: 0 };
      const newPage = await agent.Course.newPage(newPageRequest);
      this.newPageCoursePageID = newPage.coursePageID.toString();
      //await TripleTemplate(newPage.pageID);
      await this.loadCourseViewModel(this.courseViewModel.courseID);
      return newPage;
    }
  };

  newEmptyPage = async (index: number, parentCoursePageID?: number, title?: string) => {
    if (this.courseViewModel) {
      const newPageRequest: INewPageModel = { courseID: this.courseViewModel.courseID, parentCoursePageID: parentCoursePageID, index: index, title: title ? title : 'New Page', templateNo: null };
      const newPage = await agent.Course.newPage(newPageRequest);
      agent.Page.changePageLayout(newPage.courseID, newPage.coursePageID, 1);
      this.newPageCoursePageID = newPage.coursePageID.toString(); //This sets the newPageCoursePageID so it can be used to rename the page when it is created.
      await this.loadCourseViewModel(this.courseViewModel.courseID);
      return newPage;
    }
  };

  removePage = async (coursePageID: string) => {
    if (this.courseViewModel) {
      await agent.Course.removePage(this.courseViewModel.courseID, coursePageID);
      // SnackbarUtils.success('Course page deleted', () => console.log('undo page delete'), 'undo', 5000);
      SnackbarUtils.success('Course page deleted', undefined, undefined, 5000);
      this.loadCourseViewModel(this.courseViewModel.courseID);

      if (coursePageID === this.pagePropertiesPageID.toString()) {
        this.deselectPageProperties();
      }
    }
  };

  getPageProperties = async (courseId: number, coursePageId: number) => {
    const properties = await agent.Page.getProperties(courseId, coursePageId);

    runInAction(async () => {
      this.pagePropertiesPageID = coursePageId;
      this.pageProperties = properties;
    });
  };

  deselectPageProperties = async () => {
    runInAction(async () => {
      this.pagePropertiesPageID = 0;
      this.pageProperties = undefined;
    });
  };

  updatePagePropertiesTitle = async (aTitle: string) => {
    this.coursePageTempTitle = aTitle;
    runInAction(async () => {
      if (!this.pageProperties) return;
      this.pageProperties.descriptions.items.title.option.text = aTitle;
      this.pageProperties.descriptions.items.title.option.value = aTitle;
    });
  };

  getExportPackages = async (aCourseID: number): Promise<ICourseExportPackages[]> => {
    const exportPackages = await agent.ExportStaticHtml.GetExportPackages(aCourseID);

    //sort exportPackages by newest date first
    exportPackages.sort((a, b) => {
      const aDate = new Date(a.date);
      const bDate = new Date(b.date);
      return bDate.getTime() - aDate.getTime();
    });
    return exportPackages;
  };

  getExportPackageFile = (aCourseID: number, aExportID: number) => {
    agent.ExportStaticHtml.GetExportPackageFile(aCourseID, aExportID);
  };

  purgeExportPackage = (aCourseID: number, aExportID: number) => {
    return agent.ExportStaticHtml.PurgeExportPackage(aCourseID, aExportID);
  };

  //language string må være feks nb-no.
  generateExport = async (version: eExportType, language: string) => {
    if (!this.courseViewModel) return;
    const courseID = this.courseViewModel.courseID;

    if (version === eExportType.html) {
      await agent.ExportStaticHtml.ExportToHTML(courseID, '', language);
      return await this.getExportPackages(courseID);
    } else {
      await agent.ExportStaticHtml.ExportToSCORM(courseID, version, language);
      return await this.getExportPackages(courseID);
    }
  };

  copyPage = (aCopyObject: ICopyPageObject) => {
    agent.Course.copyPage(aCopyObject)
      .then((copyResponse) => {
        if (!this.courseViewModel) return;
        if (!copyResponse) return;
        this.loadCourseViewModel(this.courseViewModel.courseID);
        this.getPageProperties(this.courseViewModel.courseID, copyResponse.source.coursePageID);
      })
      .catch((error) => {
        console.error('error copying page:', error);
      });
  };

  deselectCourseProperties = () => {
    runInAction(() => {
      this.courseProperties = undefined;
      this.selectedCourseId = 0;
    });
  };

  resetNewPageCoursePageID = () => {
    this.newPageCoursePageID = '0';
  };

  setLoadingInitial = (state: boolean) => {
    this.loadingInitial = state;
  };

  setLoadingExtra = (state: boolean) => {
    this.loadingExtra = state;
  };
}
